import {
  type ColumnPinningPosition,
  ColumnPinningState,
  functionalUpdate,
  memo,
} from '@tanstack/react-table';
import { isEqual } from 'lodash-es';
import { useCallback, useMemo, useRef } from 'react';

import type {
  DataTableColumnPinningProps,
  DataTablePinnedColumnState,
} from './column-pinning-types.js';
import { pinColumns } from './column-pinning-utils.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import {
  DATA_TABLE_BUILT_IN_COLUMN_IDS,
  DATA_TABLE_ROW_ACTIONS_COLUMN_ID,
  DATA_TABLE_ROW_DETAILS_COLUMN_ID,
  DATA_TABLE_ROW_DRAG_COLUMN_ID,
  DATA_TABLE_ROW_SELECTION_COLUMN_ID,
} from '../../constants.js';
import { isGroupColumnDef } from '../../hooks/useTable/datatable-typeguards.js';
import type {
  Column,
  Header,
  Table,
  TableFeature,
  TableOptions,
  Updater,
} from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';
import { DataTableRowData } from '../../public.api.js';
import { DataTableColumnOrderProps } from '../ColumnOrder/column-order-types.js';
import { getPrefixColumnWidth } from '../Layout/layout-utils.js';

/**
 * Computes the left pinned position based on the widths of all preceding left pinned columns.
 * @internal
 */
function computeLeftPinnedPosition<TData extends DataTableRowData>(
  columnId: string,
  table: Table<TData>,
): number | undefined {
  const leftVisibleLeafColumns = table.getLeftVisibleLeafColumns();
  const targetColumnIndex = leftVisibleLeafColumns.findIndex(
    (leaf) => leaf.id === columnId,
  );

  // column is either not pinned to the left or currently hidden
  if (targetColumnIndex < 0) {
    return undefined;
  }

  // the first pinned column, no need to calculate any offset
  if (targetColumnIndex === 0) {
    return 0;
  }

  const prevColumn = leftVisibleLeafColumns[targetColumnIndex - 1];
  const isPrefix = prevColumn.columnDef.meta?.builtinPlacement === 'prefix';

  const width = isPrefix
    ? // prefix columns have a fixed width
      // use that instead of measured header, as prefix columns are also pinned when headers are hidden
      getPrefixColumnWidth(
        prevColumn,
        table.getVisualVariantOptions().rowDensity,
        table.options.rowThresholds,
      )
    : table.getColumnOrHeaderWidth(prevColumn.id);

  const prevPosition = prevColumn.getLeftPinnedPosition() ?? 0;

  // left pinned position of the preceding column plus the preceding column's width
  // is the left position for the current column
  return prevPosition + width;
}

/**
 * Computes the right pinned position based on the widths of all following right pinned columns.
 * @internal
 */
function computeRightPinnedPosition<TData extends DataTableRowData>(
  columnId: string,
  table: Table<TData>,
): number | undefined {
  const rightVisibleLeafColumns = table.getRightVisibleLeafColumns();
  const targetColumnIndex = rightVisibleLeafColumns.findIndex(
    (leaf) => leaf.id === columnId,
  );

  // column is either not pinned to the right or currently hidden
  if (targetColumnIndex < 0) {
    return undefined;
  }

  // the last pinned column, no need to calculate any offset
  if (targetColumnIndex === rightVisibleLeafColumns.length - 1) {
    return 0;
  }

  const nextColumn = rightVisibleLeafColumns[targetColumnIndex + 1];
  const nextPosition = nextColumn.getRightPinnedPosition() ?? 0;

  // right pinned position for the next column plus the next column's width
  // is the right position for the current column
  return nextPosition + table.getColumnOrHeaderWidth(nextColumn.id);
}

/**
 * Additional feature implementation for column pinning.
 * @internal
 */
export const DataTableColumnPinning: TableFeature = {
  createTable<TData extends DataTableRowData>(table: Table<TData>): void {
    table.getOrderedHeaderGroups = memo(
      () => {
        const state = table.getState();
        return [
          table.options.columns,
          state.columnVisibility,
          state.columnPinning,
          state.columnOrder,
        ];
      },
      () => {
        const leftGroups = table.getLeftHeaderGroups();
        const centerGroups = table.getCenterHeaderGroups();
        const rightGroups = table.getRightHeaderGroups();

        const getOrderedHeaderGroup = (groupIndex: number) => [
          ...(leftGroups.at(groupIndex)?.headers ?? []),
          ...(centerGroups.at(groupIndex)?.headers ?? []),
          ...(rightGroups.at(groupIndex)?.headers ?? []),
        ];

        return [getOrderedHeaderGroup(0), getOrderedHeaderGroup(1)] as const;
      },
      getMemoOptions(
        table.options,
        'debugColumns',
        'column.getOrderedHeaderGroups',
      ),
    );
  },

  createHeader<TData extends DataTableRowData, TValue>(
    header: Header<TData, TValue>,
    table: Table<TData>,
  ) {
    header.getIsPinned = () => {
      // if this is a group header, determine its pinned state based on its subHeaders
      if (isGroupColumnDef(header.column.columnDef)) {
        // all sub headers have the same pinned state, so we can just target the first one
        const firstChild = header.subHeaders.at(0);
        return firstChild?.getIsPinned() ?? false;
      }
      return header.column.getIsPinned();
    };

    header.getLeftPinnedPosition = () => {
      // if this is a group header, determine its left position based on its leftmost subHeader
      if (isGroupColumnDef(header.column.columnDef)) {
        const firstChild = header.subHeaders.at(0);
        return firstChild?.column.getLeftPinnedPosition();
      }
      return header.column.getLeftPinnedPosition();
    };

    header.getRightPinnedPosition = () => {
      // if this is a group header, determine its left position based on its rightmost subHeader
      if (isGroupColumnDef(header.column.columnDef)) {
        const lastChild = header.subHeaders.at(-1);
        return lastChild?.column.getRightPinnedPosition();
      }
      return header.column.getRightPinnedPosition();
    };

    header.getIsLastVisibleLeftPinnedHeader = () => {
      if (isGroupColumnDef(header.column.columnDef)) {
        const lastChild = header.subHeaders.at(-1);
        return lastChild?.column.getIsLastVisibleLeftPinnedColumn() ?? false;
      }
      return header.column.getIsLastVisibleLeftPinnedColumn();
    };

    header.getIsFirstVisibleUnpinnedHeader = () => {
      if (isGroupColumnDef(header.column.columnDef)) {
        const firstChild = header.subHeaders.at(0);
        return firstChild?.column.getIsFirstVisibleUnpinnedColumn() ?? false;
      }
      return header.column.getIsFirstVisibleUnpinnedColumn();
    };

    header.getIsLastVisibleUnpinnedHeader = () => {
      if (isGroupColumnDef(header.column.columnDef)) {
        const lastChild = header.subHeaders.at(-1);
        return lastChild?.column.getIsLastVisibleUnpinnedColumn() ?? false;
      }
      return header.column.getIsLastVisibleUnpinnedColumn();
    };

    header.getIsFirstVisibleRightPinnedHeader = () => {
      if (isGroupColumnDef(header.column.columnDef)) {
        const firstChild = header.subHeaders.at(0);
        return firstChild?.column.getIsFirstVisibleRightPinnedColumn() ?? false;
      }
      return header.column.getIsFirstVisibleRightPinnedColumn();
    };

    header.pin = (position: ColumnPinningPosition) => {
      const { column, subHeaders: visibleSubHeaders } = header;
      const idsToPin: string[] = isGroupColumnDef(column.columnDef)
        ? visibleSubHeaders.map((subHeader) => subHeader.column.id)
        : [column.id];

      const pinningState = pinColumns(
        idsToPin,
        table.getState().columnPinning,
        position,
      );

      table.setColumnPinning(pinningState);
    };
  },

  createColumn<TData extends DataTableRowData>(
    column: Column<TData, unknown>,
    table: Table<TData>,
  ) {
    // cache left pinned position to avoid recalculating during resizing
    let cachedLeftPinnedPosition: number | undefined;
    column.getLeftPinnedPosition = memo(
      () => {
        const state = table.getState();
        return [
          state.columnSizingInfo.isResizingColumn,
          state.columnVisibility,
          state.columnPinning.left
            ?.map((id) => `${id}:${table.getMeasuredColumnWidth(id)}`)
            .join(),
          table.options.columns,
        ];
      },
      (isResizingColumn) => {
        // prevent recalculation while resizing column to avoid jumpy behavior
        if (isResizingColumn) {
          if (cachedLeftPinnedPosition === undefined) {
            cachedLeftPinnedPosition = computeLeftPinnedPosition(
              column.id,
              table,
            );
          }
          return cachedLeftPinnedPosition;
        }
        cachedLeftPinnedPosition = undefined;
        return computeLeftPinnedPosition(column.id, table);
      },
      getMemoOptions(
        table.options,
        'debugColumns',
        'column.getLeftPinnedPosition',
      ),
    );

    // cache right pinned position to avoid recalculating during resizing
    let cachedRightPinnedPosition: number | undefined;
    column.getRightPinnedPosition = memo(
      () => {
        const state = table.getState();
        return [
          state.columnSizingInfo.isResizingColumn,
          state.columnVisibility,
          state.columnPinning.right
            ?.map((id) => `${id}:${table.getMeasuredColumnWidth(id)}`)
            .join(),
          table.options.columns,
        ];
      },
      (isResizingColumn) => {
        // prevent recalculation while resizing column to avoid jumpy behavior
        if (isResizingColumn) {
          if (cachedRightPinnedPosition === undefined) {
            cachedRightPinnedPosition = computeRightPinnedPosition(
              column.id,
              table,
            );
          }
          return cachedRightPinnedPosition;
        }
        cachedRightPinnedPosition = undefined;
        return computeRightPinnedPosition(column.id, table);
      },
      getMemoOptions(
        table.options,
        'debugColumns',
        'column.getRightPinnedPosition',
      ),
    );

    column.getIsLastVisibleLeftPinnedColumn = memo(
      () => {
        const state = table.getState();
        return [
          state.columnPinning.left,
          state.columnVisibility,
          table.options.columns,
        ];
      },
      () => {
        // return true if the current column or one of its child columns is the last visible left pinned column
        const lastLeftVisibleLeafColumnId = table
          .getLeftVisibleLeafColumns()
          .at(-1)?.id;
        return (
          column.id === lastLeftVisibleLeafColumnId ||
          column.columns.some(
            (childColumn) => childColumn.id === lastLeftVisibleLeafColumnId,
          )
        );
      },
      getMemoOptions(
        table.options,
        'debugColumns',
        'column.getIsLastVisibleLeftPinnedColumn',
      ),
    );

    column.getIsFirstVisibleUnpinnedColumn = memo(
      () => {
        const state = table.getState();
        return [
          state.columnPinning,
          state.columnVisibility,
          state.columnOrder,
          table.options.columns,
        ];
      },
      () => {
        // return true if the current column or one of its child columns is the first visible, unpinned column
        const firstCenterVisibleLeafColumnId = table
          .getCenterVisibleLeafColumns()
          .at(0)?.id;
        return (
          column.id === firstCenterVisibleLeafColumnId ||
          column.columns.some(
            (childColumn) => childColumn.id === firstCenterVisibleLeafColumnId,
          )
        );
      },
      getMemoOptions(
        table.options,
        'debugColumns',
        'column.getIsFirstVisibleUnpinnedColumn',
      ),
    );

    column.getIsLastVisibleUnpinnedColumn = memo(
      () => {
        const state = table.getState();
        return [
          state.columnPinning,
          state.columnVisibility,
          state.columnOrder,
          table.options.columns,
        ];
      },
      () => {
        // return true if the current column or one of its child columns is the last visible unpinned column
        const lastCenterVisibleLeafColumnId = table
          .getCenterVisibleLeafColumns()
          .at(-1)?.id;
        return (
          column.id === lastCenterVisibleLeafColumnId ||
          column.columns.some(
            (childColumn) => childColumn.id === lastCenterVisibleLeafColumnId,
          )
        );
      },
      getMemoOptions(
        table.options,
        'debugColumns',
        'column.getIsLastVisibleUnpinnedColumn',
      ),
    );

    column.getIsFirstVisibleRightPinnedColumn = memo(
      () => {
        const state = table.getState();
        return [
          state.columnPinning.right,
          state.columnVisibility,
          table.options.columns,
        ];
      },
      () => {
        // return true if the current column or one of its child columns is the first visible right pinned column
        const firstRightVisibleLeafColumnId = table
          .getRightVisibleLeafColumns()
          .at(0)?.id;
        return (
          column.id === firstRightVisibleLeafColumnId ||
          column.columns.some(
            (childColumn) => childColumn.id === firstRightVisibleLeafColumnId,
          )
        );
      },
      getMemoOptions(
        table.options,
        'debugColumns',
        'column.getIsFirstVisibleRightPinnedColumn',
      ),
    );
  },
};

/**
 * @returns true, if the given column is not a built-in column, false otherwise.
 */
function isCustomColumn(columnId: string): boolean {
  return !DATA_TABLE_BUILT_IN_COLUMN_IDS.has(columnId);
}

function removeBuiltinColumns(pinnedColumns: string[] | undefined): string[] {
  return pinnedColumns?.filter(isCustomColumn) ?? [];
}

function addBuiltinColumns(
  pinnedColumns: DataTablePinnedColumnState | undefined,
  options: {
    enableRowDetails: boolean;
    enableRowSelection: boolean;
    enableRowOrderDragAndDrop: boolean;
    enableRowActions: boolean;
  },
): DataTablePinnedColumnState {
  if (!pinnedColumns) {
    return {};
  }

  // remove any built-in columns first
  const leftPinnedColumns = removeBuiltinColumns(pinnedColumns.left);
  const rightPinnedColumns = removeBuiltinColumns(pinnedColumns.right);

  // add built-in columns based on enabled features, in correct pinning order
  if (options.enableRowDetails) {
    leftPinnedColumns.unshift(DATA_TABLE_ROW_DETAILS_COLUMN_ID);
  }
  if (options.enableRowSelection) {
    leftPinnedColumns.unshift(DATA_TABLE_ROW_SELECTION_COLUMN_ID);
  }
  if (options.enableRowOrderDragAndDrop) {
    leftPinnedColumns.unshift(DATA_TABLE_ROW_DRAG_COLUMN_ID);
  }
  if (options.enableRowActions) {
    rightPinnedColumns.push(DATA_TABLE_ROW_ACTIONS_COLUMN_ID);
  }

  return {
    left: leftPinnedColumns,
    right: rightPinnedColumns,
  };
}

/**
 * Configuration hook for the DataTable column pinning feature.
 * @internal
 */
export function useColumnPinning<TData extends DataTableRowData>(
  props: DataTableColumnPinningProps & DataTableColumnOrderProps,
  options: TableOptions<TData>,
): void {
  options.enableColumnPinning = props.columnPinning === true;

  const {
    enableColumnPinning,
    enableRowDetails,
    enableRowSelection,
    enableRowOrderDragAndDrop,
    enableRowActions,
  } = options;

  // uncontrolled
  const defaultPinnedColumns = useMemo(() => {
    // columns pinned via props in addition to built-in columns
    const userDefinedPinnedColumns =
      enableColumnPinning && props.defaultPinnedColumns
        ? props.defaultPinnedColumns
        : {};
    return addBuiltinColumns(userDefinedPinnedColumns, {
      enableRowDetails: !!enableRowDetails,
      enableRowSelection: !!enableRowSelection,
      enableRowOrderDragAndDrop: !!enableRowOrderDragAndDrop,
      enableRowActions: !!enableRowActions,
    });
  }, [
    enableColumnPinning,
    enableRowActions,
    enableRowDetails,
    enableRowOrderDragAndDrop,
    enableRowSelection,
    props.defaultPinnedColumns,
  ]);

  // controlled
  const pinnedColumns = useMemo(
    (): DataTablePinnedColumnState | undefined =>
      enableColumnPinning && props.pinnedColumns
        ? addBuiltinColumns(props.pinnedColumns, {
            enableRowDetails: !!enableRowDetails,
            enableRowSelection: !!enableRowSelection,
            enableRowOrderDragAndDrop: !!enableRowOrderDragAndDrop,
            enableRowActions: !!enableRowActions,
          })
        : undefined,
    [
      enableColumnPinning,
      enableRowActions,
      enableRowDetails,
      enableRowOrderDragAndDrop,
      enableRowSelection,
      props.pinnedColumns,
    ],
  );

  const [columnPinning, setColumnPinning] =
    useControllableState<ColumnPinningState>({
      value: pinnedColumns,
      defaultValue: defaultPinnedColumns,
    });

  options.state = {
    ...options.state,
    columnPinning: enableColumnPinning ? columnPinning : defaultPinnedColumns, // just built-in columns
  };

  options.onColumnPinningChange = useCallback(
    (updater: Updater<ColumnPinningState>) => {
      const nextState = functionalUpdate(updater, columnPinning);
      if (isEqual(nextState, columnPinning)) {
        // nothing to update
        return;
      }

      // user does not have to provide IDs of built-in columns (which are always pinned)
      setColumnPinning(
        addBuiltinColumns(nextState, {
          enableRowDetails: !!enableRowDetails,
          enableRowSelection: !!enableRowSelection,
          enableRowOrderDragAndDrop: !!enableRowOrderDragAndDrop,
          enableRowActions: !!enableRowActions,
        }),
      );
      // do not emit IDs of built-in columns (which are always pinned)
      props.onPinnedColumnsChange?.({
        left: removeBuiltinColumns(nextState.left),
        right: removeBuiltinColumns(nextState.right),
      });
    },
    [
      columnPinning,
      enableRowActions,
      enableRowDetails,
      enableRowOrderDragAndDrop,
      enableRowSelection,
      props,
      setColumnPinning,
    ],
  );

  const originalColumnPinningRef = useRef<DataTablePinnedColumnState>();

  if (!originalColumnPinningRef.current) {
    const originalPinned = pinnedColumns || defaultPinnedColumns || [];
    originalColumnPinningRef.current = {
      left: originalPinned.left,
      right: originalPinned.right,
    };
  }

  options.originalColumnPinning = originalColumnPinningRef.current;
}
